/**
 * Outage details: automatic customer notifications. Shows the current mode, lets staff mute this outage, and
 * lists everything that was sent (or would have been, in test mode).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Chip, FormControlLabel, Switch, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import apiService from '../services/api';

interface Item {
  id: string;
  event: string;
  status: 'sent' | 'test' | 'failed' | 'skipped' | 'held';
  account_name: string | null;
  to_emails: string | null;
  service_ids: string | null;
  error: string | null;
  created_at: string;
}

const STATUS_COLOR: Record<string, 'success' | 'info' | 'error' | 'default' | 'warning'> = {
  sent: 'success', test: 'info', failed: 'error', skipped: 'default', held: 'warning',
};
const MODE_LABEL = { off: 'Off', test: 'Test mode (customers are not emailed)', live: 'Live' } as const;

const OutageCustomerNotifications: React.FC<{ outageId: string; refreshKey?: number }> = ({ outageId, refreshKey }) => {
  const [data, setData] = useState<{ mode: 'off' | 'test' | 'live'; notify_customers: boolean; items: Item[] } | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setData(await apiService.getOutageCustomerNotifications(outageId));
    } catch {
      setError('Could not load customer notifications.');
    }
  }, [outageId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const toggle = async (enabled: boolean) => {
    setError('');
    try {
      await apiService.setOutageNotifyCustomers(outageId, enabled);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not change this setting.');
    }
  };

  if (!data) return error ? <Alert severity="error">{error}</Alert> : null;

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <FormControlLabel
          control={<Switch size="small" checked={data.notify_customers} onChange={(e) => toggle(e.target.checked)} />}
          label="Notify customers automatically (new outage / planned notice / resolved)"
        />
        <Chip size="small" color={data.mode === 'live' ? 'success' : data.mode === 'test' ? 'warning' : 'default'} label={`Automatic notifications: ${MODE_LABEL[data.mode]}`} />
      </Box>
      {!data.notify_customers && <Alert severity="info" sx={{ mt: 1 }}>Customers will not be notified automatically about this outage.</Alert>}
      {error && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError('')}>{error}</Alert>}
      {data.items.length > 0 && (
        <Box sx={{ mt: 1.5, maxHeight: 220, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Notice</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>Emailed</TableCell>
                <TableCell>Result</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.items.map((n) => (
                <TableRow key={n.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(n.created_at).toLocaleString('en-AU')}</TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{n.event}</TableCell>
                  <TableCell>{n.account_name || 'All affected accounts'}</TableCell>
                  <TableCell>{n.to_emails || '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" color={STATUS_COLOR[n.status]} label={n.status} sx={{ textTransform: 'capitalize' }} />
                    {n.error && <Typography variant="caption" color="text.secondary" display="block">{n.error}</Typography>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default OutageCustomerNotifications;
